import { Grid, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import * as taskService from "source/models/services/taskService";
import { TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { optionCodeTaskTypeExpressionInfoDocument } from "ui/Scripts/source/models/consts";
import { defaultExpression, ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { AppUtils } from "source/framework/appUtils";

interface IFormDesignerComponentPropertiesExpressionProps {
    formFieldMargin: string;
    isRequired: boolean;
    show: boolean;
    field: string;
    expression: string;
    expressionPropertyName: string;
    expressionFormat: string;
    showExpressionFormat: boolean;
    taskTypeCols: ITaskTypeCol[];
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

export const FormDesignerComponentPropertiesExpression = (
    props: IFormDesignerComponentPropertiesExpressionProps
) => {
    const functionList = [
        { id: "0", title: `'${Translations.SelectOperatorOrFunction}'` },
        { id: "D1", title: Translations.Operators, divider: true },
        { id: "1", title: Translations.FormulaAddition, value: "number1 + number2" },
        { id: "2", title: Translations.FormulaSubtraction, value: "number1 - number2" },
        { id: "3", title: Translations.FormulaMultiplication, value: "number1 * number2" },
        { id: "4", title: Translations.FormulaDivision, value: "number1 / number2" },
        { id: "5", title: Translations.FormulaRemainder, value: "number1 % number2" },
        { id: "6", title: Translations.FormulaExponent, value: "number1 ** number2" },
        { id: "D2", title: Translations.Functions, divider: true },
        { id: "7", title: Translations.FormulaMax, value: "Math.max(number1,number2)" },
        { id: "8", title: Translations.FormulaMin, value: "Math.min(number1,number2)" },
        { id: "9", title: Translations.FormulaAbs, value: "Math.abs(number)" },
        { id: "10", title: Translations.FormulaCeil, value: "Math.ceil(number)" },
        { id: "11", title: Translations.FormulaFloor, value: "Math.floor(number)" },
        { id: "12", title: Translations.FormulaTrunc, value: "Math.trunc(number)" },
        { id: "13", title: Translations.FormulaRound, value: "Math.round(number)" },
        { id: "D3", title: Translations.Trigonometry, divider: true },
        { id: "14", title: Translations.FormulaExponent, value: "Math.pow(number,2)" },
        { id: "15", title: Translations.FormulaSqrt, value: "Math.sqrt(number)" },
        { id: "16", title: Translations.FormulaCbrt, value: "Math.cbrt(number)" },
        { id: "17", title: Translations.FormulaCos, value: "Math.cos(number)" },
        { id: "18", title: Translations.FormulaSin, value: "Math.sin(number)" },
        { id: "19", title: Translations.FormulaTan, value: "Math.tan(number)" },
        { id: "20", title: Translations.FormulaHypot, value: "Math.hypot(number1, number2)" },
        { id: "21", title: Translations.FormulaPI, value: "Math.PI" },
        { id: "D4", title: Translations.Conversions, divider: true },
        { id: "22", title: Translations.FormulaDateAndTimeDiffInMinutes, value: "utcDateAndTimeDiffInMinutes(date1,time1,date2,time2)" },
        { id: "23", title: Translations.FormulaDateAndTimeDiffInTimeSpan, value: "utcDateAndTimeDiffInTimeSpan(date1,time1,date2,time2)" },
    ];
    const [expression, setExpression] = useState("");
    const [selectedFunctionId, setSelectedFunctionId] = useState("0");

    const setFieldsToFunctionByDataType = (dataTypes: TaskDataRowDataType[], prefix: string, functionValue: string) => {
        let result = functionValue;
        if (!result.includes(prefix)) return result;
        const fields = props.taskTypeCols.filter((c) => dataTypes.includes(c.dataType));
        if (!fields.length) return result;
        for (let i = 0; i < fields.length; i++) {
            result = result.replace(`${prefix}${i + 1}`, fields[i].field);
        }
        return result.replace(prefix, fields[0].field);
    };

    const setFieldsToFunction = (functionValue: string) => {
        return setFieldsToFunctionByDataType([TaskDataRowDataType.Time], "time",
            setFieldsToFunctionByDataType([TaskDataRowDataType.Date], "date",
                setFieldsToFunctionByDataType([TaskDataRowDataType.Integer, TaskDataRowDataType.Number], "number", functionValue)
            ));
    };

    const handleAddFunction = () => {
        const functionValue = functionList.find((f) => f.id === selectedFunctionId)?.value;
        if (!functionValue) return;
        const newExpression = setFieldsToFunction(functionValue);
        setExpression(newExpression);
        props.onTaskTypePropUpdate([{
            field: props.field,
            name: props.expressionPropertyName,
            value: newExpression
        }]);
    };

    useEffect(() => {
        setExpression(props.expression ?? "");
        const selectedFunction = functionList.find((f) => f.value === props.expression);
        setSelectedFunctionId(selectedFunction ? selectedFunction.id : "0");
    }, [props.expression]);

    const expressionFormats: IIdTitle[] = [
        IdTitle.createIdTitle("", "'" + Translations.NotInUse + "'"),
        IdTitle.createIdTitle("minutesToDateStr(fieldValue)", "'" + Translations.FormExpressionFormatMinutesToHoursAndMinutes + "'")
    ];

    const handleChangeExpression = (event) => {
        const value: string = event.target.value;
        setExpression(value);
    };

    const handleBlurExpression = (event) => {
        let value: string = event.target.value;
        if (!value) {
            value = defaultExpression;
            setExpression(value);
        }
        props.onTaskTypePropUpdate([{
            field: props.field,
            name: props.expressionPropertyName,
            value
        }]);
    };

    if (!props.show) return null;
    return (
        <>
            <Grid item sm={12}>
                <FormField
                    title={Translations.FormExpression}
                    margin={props.formFieldMargin}
                    required={props.isRequired}
                    getInfoDocument={async () => {
                        const fileName = await AppUtils.callService(() => taskService.downloadInfoDocument(optionCodeTaskTypeExpressionInfoDocument));
                        if (!fileName) return;
                        AppUtils.showSuccessMessage(String.format(Translations.DocumentParameterDownloaded, fileName));
                    }}
                >
                    <IdTitleDropdown
                        selectedId={selectedFunctionId}
                        items={functionList.map(i => { return { id: i.id, title: i.title, subTitle: i.value ? `(${i.value})` : null, divider: i.divider }; })}
                        sx={{ marginBottom: ".5rem" }}
                        onChange={(id: string) => setSelectedFunctionId(id)}
                        buttons={[
                            { label: Translations.Add, onClick: handleAddFunction, disabled: selectedFunctionId === "0" }]
                        }
                    />
                    <TextField
                        name="expression"
                        title={Translations.FormExpression}
                        inputProps={{ maxLength: 10000 }}
                        value={expression}
                        onChange={handleChangeExpression}
                        onBlur={handleBlurExpression}
                        multiline
                        rows={5}
                        size="small"
                        fullWidth
                    />
                </FormField>
            </Grid>
            {props.showExpressionFormat &&
                <Grid item sm={12}>
                    <FormField title={Translations.FormExpressionFormat} margin={props.formFieldMargin}>
                        <IdTitleDropdown
                            selectedId={props.expressionFormat ?? ""}
                            items={expressionFormats}
                            onChange={(value) => props.onTaskTypePropUpdate([{
                                field: props.field,
                                name: "expressionFormat",
                                value
                            }])}
                        />
                    </FormField>
                </Grid>
            }
        </>
    );
};
